import {
  ApolloClient,
  ApolloClientOptions,
  ApolloLink,
  CombinedGraphQLErrors,
  HttpLink,
  InMemoryCache,
  ServerError,
  from,
} from '@apollo/client';
import type { GraphQLFormattedError } from 'graphql';
import { onError } from '@apollo/client/link/error';
import { RestLink } from 'apollo-link-rest';
import { setContext } from '@apollo/client/link/context';
import * as Sentry from '@sentry/react';

// Apollo 4 requires module augmentation to whitelist any `defaultOptions` keys
// we set on the client. Without these, the typed `defaultOptions` literal is
// rejected with an error message embedded in the type.
declare module '@apollo/client' {
  namespace ApolloClient.DeclareDefaultOptions {
    interface WatchQuery {
      fetchPolicy: 'no-cache';
    }
    interface Query {
      fetchPolicy: 'no-cache';
      errorPolicy: 'all';
    }
  }
}

interface UseClientOptions {
  clientOptions?: Partial<ApolloClientOptions>;
}

// Mercury (Absinthe) puts `code`, `sub_code` and `parameter` at the top level
// of each GraphQL error rather than under `extensions`.
type MercuryGraphQLError = GraphQLFormattedError & {
  code?: string;
  sub_code?: string;
  parameter?: { name?: string };
};

export function makeSentryLink(onUnauthorized?: () => void) {
  return onError(({ error, operation }) => {
    const operationName = operation.operationName ?? 'anonymous';

    // Apollo 4 consolidated `graphQLErrors`/`networkError` into a single
    // `error` field. `CombinedGraphQLErrors` carries the per-field GraphQL
    // errors array when the failure originated server-side.
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach((graphQLError) => {
        const { message, path, locations, code, sub_code, parameter } =
          graphQLError as MercuryGraphQLError;

        // Validation failures and expired sessions are expected; keep them as
        // breadcrumbs for context instead of reporting them as errors.
        if (code === 'parameter_error' || code === 'unauthorized') {
          Sentry.addBreadcrumb({
            category: 'graphql',
            level: 'info',
            message,
            data: { operationName, code, parameter: parameter?.name },
          });
          if (code === 'unauthorized') onUnauthorized?.();
          return;
        }

        // GraphQL errors are plain objects; wrap them so Sentry gets a real
        // title, and group per operation + code rather than all together.
        const exception = new Error(`[GraphQL] ${operationName}: ${message}`);
        exception.name = 'GraphQLError';
        Sentry.captureException(exception, {
          tags: { 'graphql.operation': operationName, 'graphql.code': code ?? 'none' },
          fingerprint: ['graphql', operationName, code ?? message],
          // Variable values can hold PII (contact names, emails, phones).
          extra: {
            path,
            locations,
            parameter,
            sub_code,
            variableKeys: Object.keys(operation.variables ?? {}),
          },
        });
      });
    } else if (ServerError.is(error) && error.statusCode === 401) {
      Sentry.addBreadcrumb({
        category: 'graphql',
        level: 'info',
        message: error.message,
        data: { operationName, statusCode: error.statusCode },
      });
      onUnauthorized?.();
    } else {
      Sentry.captureException(error);
    }
  });
}

export function makeClient({
  clientOptions = {},
  authorization,
  onUnauthorized,
}: UseClientOptions & { authorization: string | null; onUnauthorized?: () => void }) {
  const httpLink: ApolloLink = new HttpLink({
    uri: import.meta.env.VITE_GQL_ENDPOINT,
  });
  const authLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        authorization,
      },
    };
  });

  // Only a request that carried a token can mean the session died mid-use.
  const sentryLink = makeSentryLink(authorization ? onUnauthorized : undefined);

  const restLink = new RestLink({
    uri: `${import.meta.env.VITE_BASE}`,
    endpoints: {
      export: `${import.meta.env.VITE_BASE}/export/`,
      summarize: `${import.meta.env.VITE_BASE}/summarize/`,
    },
    headers: {
      'Content-Type': 'application/json',
    },

    responseTransformer: async (data: unknown) => {
      if (data instanceof Response) {
        const dataHeaders = data.headers.get('content-type');
        if (dataHeaders?.includes('application/csv')) {
          const dataText = await data.text();
          return {
            data: dataText,
          };
        } else {
          const dataJson = await data.json();
          return {
            data: dataJson,
          };
        }
      }

      return data;
    },
  });

  const link = from([sentryLink, authLink, restLink, httpLink]);
  const cache = new InMemoryCache({});

  return new ApolloClient({
    link,
    cache,
    // This will temporary disable query force-fetching
    ssrForceFetchDelay: 100,
    devtools: {
      name: 'mercury-public-client',
      enabled: process.env.NODE_ENV !== 'production',
    },
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
      },
      query: {
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
      },
    },
    ...clientOptions,
  });
}
